import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminHeader from '../../components/headers/AdminHeader';
import AgencyRegistrationPop from '../../components/popups/AgencyRegistrationPop';
import AgencyInfoTab from '../../tabs/agencyRegistration/AgencyInfoTab';
import AgencySolicitorsTab from '../../tabs/agencyRegistration/AgencySolicitorsTab';
import { createAgency } from '../../services/api/AgencyService';
import { fetchDocumentTypes } from '../../services/api/DocumentTypeService';
import { createSolicitor, importSolicitorsFromCSV, exportSolicitorsToCSV } from '../../services/api/SolicitorService';
import { validateAgencyForm, displayAgencyValidationErrorsWithDialog } from '../../utils/agencyValidation';
import { validateSolicitorForm, displayValidationErrorsWithDialog } from '../../utils/solicitorValidation';
import { 
  useErrorDialog, 
  showSuccessDialog
} from '../../providers/ErrorDialogProvider';
import '../../styles/Tabs.css';
import '../../styles/AgencyRegistration.css';
import iconTitle from '../../assets/img/icon-square.svg';
import iconBtnUl from '../../assets/img/icon-ul.svg';
import iconBtnDl from '../../assets/img/icon-dl.svg';
import { formatDateForApi } from '../../utils/dateFormatter';
import { buildDocumentTypesForApi, getDocumentTypeSelectionKey, normalizeDocumentTypes } from '../../utils/documentTypeUtils';

// 代理店登録ページコンポーネント
// ※pages/agencyにも同様のコンポーネントがあるため、修正時は同時に確認すること
const AgencyRegistration = () => {
  const { agencyId } = useParams();
  const navigate = useNavigate();
  const { showError } = useErrorDialog();
  const [agencyData, setAgencyData] = useState(null);
  const [activeTab, setActiveTab] = useState('info');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    tradingCode: '',
    name: '',
    nameKana: '',
    contractDate: '',
    cancellationDate: '',
    postalCode: '',
    prefecture: '',
    city: '',
    building: '',
    phoneNumber: '',
    accountname: '',
    documentTypeIds: []
  });
  const [documentTypes, setDocumentTypes] = useState([]);
  const [solicitorForm, setSolicitorForm] = useState({
    branchName: '',
    name: '',
    nameKana: '',
    startDate: '',
    endDate: '',
    profile: '',
    accountname: '',
  });

  // 代理店IDが存在するかどうかのフラグ
  const hasAgencyId = agencyId !== undefined && agencyId !== null && agencyId !== '';

  // 確認ポップアップの表示状態を管理する状態変数を追加
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);

  // 新規登録画面なので代理店データの取得は不要
  const initializeAgencyData = () => {
    // 新規登録用の初期データを設定
    setAgencyData({
      name: '新規登録'
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // 新規登録用の初期データを設定
        initializeAgencyData();

        // 文書種別マスタ（アクセス権設定用）を取得
        const documentTypeResponse = await fetchDocumentTypes();
        const normalizedTypes = normalizeDocumentTypes(documentTypeResponse);
        setDocumentTypes(normalizedTypes);
        setFormData((prev) => {
          if (prev.documentTypeIds?.length) {
            return prev;
          }
          const defaultDocumentTypeKeys = normalizedTypes
             .filter((type) => type?.insurance_type != null && type?.code)
             .map((type) => getDocumentTypeSelectionKey(type));
          return {
            ...prev,
            documentTypeIds: defaultDocumentTypeKeys
          };
        });

        // 代理店IDが存在する場合は、タブを募集人に切り替え
        if (hasAgencyId) {
          setActiveTab('solicitors');
        }
        
      } catch (error) {
        // エラーダイアログ表示はBaseApiServiceで行う
        // エラーコンソール出力は各ApiServiceで行う
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [hasAgencyId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (activeTab === 'info') {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    } else if (activeTab === 'solicitors') {
      setSolicitorForm(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleDocumentTypeToggle = (documentTypeId) => {
    setFormData((prev) => {
      const current = new Set(prev.documentTypeIds || []);
      if (current.has(documentTypeId)) {
        current.delete(documentTypeId);
      } else {
        current.add(documentTypeId);
      }
      return {
        ...prev,
        documentTypeIds: Array.from(current)
      };
    });
  };

  const handleRegister = async () => {
    // アクティブなタブに応じて異なるバリデーションを行う
    if (activeTab === 'info') {
      // 日付のフォーマットをハイフン形式からスラッシュ形式に変換
      formData.contractDate = formatDateForApi(formData.contractDate);
      formData.cancellationDate = formatDateForApi(formData.cancellationDate);
      // 代理店情報のバリデーション
      const validationResult = validateAgencyForm(formData);
      if (!displayAgencyValidationErrorsWithDialog(validationResult, showError)) {
        return;
      }
    } else if (activeTab === 'solicitors') {
      // 日付のフォーマットをハイフン形式からスラッシュ形式に変換
      solicitorForm.startDate = formatDateForApi(solicitorForm.startDate);
      solicitorForm.endDate = formatDateForApi(solicitorForm.endDate);
      // 募集人情報のバリデーション
      const validationResult = validateSolicitorForm(solicitorForm);
      if (!displayValidationErrorsWithDialog(validationResult, showError)) {
        return;
      }
    }
    
    // すべてのバリデーションが通過したら登録確認ポップアップを表示
    setShowConfirmationPopup(true);
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }
  
  // 確認ポップアップからの登録処理
  const confirmRegistration = async () => {
    setIsLoading(true);
    let registrationSuccess = false;
    let newAgencyId = null;
    
    try {
      // アクティブなタブに応じて異なる処理を行う
      if (activeTab === 'info') {
        console.log('保存されたデータ:', formData);
        
        // 再バリデーション
        const validationResult = validateAgencyForm(formData);
        if (!displayAgencyValidationErrorsWithDialog(validationResult, showError)) {
          setIsLoading(false);
          return;
        }
        
        // APIに送信するデータ形式をバックエンドの要件に合わせる
        const agencyData = {
          agency_name: formData.name,
          agency_name_kana: formData.nameKana,
          trading_code: formData.tradingCode,
          contract_date: formData.contractDate || null,
          cancellation_date: formData.cancellationDate || null,
          postal_code: formData.postalCode,
          prefecture: formData.prefecture,
          city: formData.city,
          building: formData.building || null,
          phone: formData.phoneNumber,
          accountname: formData.accountname,
          document_types: buildDocumentTypesForApi(formData.documentTypeIds, documentTypes)
        };
        
        const response = await createAgency(agencyData);
        
        // 登録成功時の処理
        console.log('代理店登録成功:', response);
        newAgencyId = response.id;
        registrationSuccess = true;
        
        // 成功メッセージを表示
        showSuccessDialog('代理店の登録が完了しました。');

      } else if (activeTab === 'solicitors') {
        console.log('募集人登録データ:', solicitorForm);
        
        // 再バリデーション
        const validationResult = validateSolicitorForm(solicitorForm);
        if (!displayValidationErrorsWithDialog(validationResult, showError)) {
          setIsLoading(false);
          return;
        }
        
        // APIに送信するデータ形式をバックエンドの要件に合わせる
        const solicitorData = {
          branch_name: solicitorForm.branchName || null,
          solicitor_name: solicitorForm.name,
          solicitor_name_kana: solicitorForm.nameKana,
          agency_id: parseInt(agencyId),
          accountname: solicitorForm.accountname,
          start_date: solicitorForm.startDate || null,
          end_date: solicitorForm.endDate || null,
          profile: solicitorForm.profile || null
        };
        
        const response = await createSolicitor(solicitorData);
        
        // 登録成功時の処理
        console.log('募集人登録成功:', response);
        registrationSuccess = true;
        
        // 成功メッセージを表示
        showSuccessDialog('募集人の登録が完了しました。');
        
        // フォームをリセット
        setSolicitorForm({
          branchName: '',
          name: '',
          nameKana: '',
          startDate: '',
          endDate: '',
          profile: '',
          accountname: '',
        });
      }

    } catch (err) {
      // エラーダイアログ表示はBaseApiServiceで行う
      // エラーコンソール出力は各ApiServiceで行う
    } finally {
      setIsLoading(false);  
      // ポップアップを閉じる
      setShowConfirmationPopup(false);
      
      // 登録が成功した場合のみリダイレクト処理を行う
      if (registrationSuccess && activeTab === 'info' && newAgencyId) {
        // 成功メッセージを表示してから遷移
        navigate(`/admin/agency/detail/${newAgencyId}`);
      }
    }
  };

  if (error) {
    return <div>{error}</div>;
  }

  // CSVアップロード用の関数
  const handleCsvUpload = () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.csv';
    fileInput.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      setIsLoading(true);
      try {
        await importSolicitorsFromCSV(agencyId, file);
        showSuccessDialog('CSVアップロードが完了しました。');
      } catch (err) {
        // エラーダイアログ表示はBaseApiServiceで行う
        // エラーコンソール出力は各ApiServiceで行う
      } finally {
        setIsLoading(false);
      }
    };
    fileInput.click();
  };

  // CSVダウンロード用の関数
  const handleCsvDownload = async () => {
    setIsLoading(true);
    try {
      await exportSolicitorsToCSV(agencyId);
      console.log('募集人CSVのダウンロードが完了しました');
    } catch (err) {
      // エラーダイアログ表示はBaseApiServiceで行う
      // エラーコンソール出力は各ApiServiceで行う
    } finally {
      setIsLoading(false);
    }
  };
  
  // 確認ポップアップを閉じる処理
  const closeConfirmationPopup = () => {
    setShowConfirmationPopup(false);
  };

  return (
    <>
      <AdminHeader />
      <div className="page-container">
        <div className="content-wrapper">
          <div className="page-header">
            <div className="title-section">
              <div className="icon-square">
                <img src={iconTitle} alt="タイトルアイコン" />
              </div>
              <h1>代理店登録：{agencyData.name}</h1>
            </div>
            {/* 募集人タブが選択されている時のみ募集人追加ボタンを表示 */}
            {activeTab === 'solicitors' && (
              <div className='solicitor-add-button-box'>
                <button 
                  className="solicitor-add-button button-small solicitor-add-button--pale-blue solicitor-add-button--img-none"
                  onClick={handleCsvUpload}
                  disabled={isLoading}
                >
                  <span>CSVアップロード</span>
                  <img src={iconBtnUl} alt="ボタンアイコン" className='solicitor-icon-img' />
                </button>
                <button 
                  className="solicitor-add-button button-small solicitor-add-button--img-none solicitor-add-button--img-none"
                  onClick={handleCsvDownload}
                  disabled={isLoading}
                >
                  <span>CSVダウンロード</span>
                  <img src={iconBtnDl} alt="ボタンアイコン" className='solicitor-icon-img' />
                </button>
              </div>
            )}
          </div>
          <div className="tab-container">
            <div className="tab-menu">
              <button 
                className={`tab-button ${activeTab === 'info' ? 'active' : ''} ${hasAgencyId ? 'disabled' : ''}`}
                onClick={() => !hasAgencyId && setActiveTab('info')}
                disabled={hasAgencyId}
              >
                代理店情報
              </button>
              <button 
                className={`tab-button ${activeTab === 'insurance' ? 'active' : ''} disabled`}
                onClick={() => {}}
                disabled={true}
              >
                登録保険会社
              </button>
              <button 
                className={`tab-button ${activeTab === 'solicitors' ? 'active' : ''} ${!hasAgencyId ? 'disabled' : ''}`}
                onClick={() => hasAgencyId && setActiveTab('solicitors')}
                disabled={!hasAgencyId}
              >
                募集人
              </button>
            </div>
            <div className="tab-content">
              {/* 代理店情報タブ */}
              {!hasAgencyId && (
                <div className={`tab-panel ${activeTab === 'info' ? 'active' : ''}`}>
                  <AgencyInfoTab 
                    formData={formData} 
                    handleChange={handleChange} 
                    navigate={navigate} 
                    handleRegister={handleRegister}
                    documentTypes={documentTypes}
                    selectedDocumentTypeIds={formData.documentTypeIds}
                    onDocumentTypeToggle={handleDocumentTypeToggle}
                  />
                </div>
              )}
              {/* 募集人タブ */}
              {hasAgencyId && (
                <div className={`tab-panel ${activeTab === 'solicitors' ? 'active' : ''}`}>
                  <AgencySolicitorsTab
                    navigate={navigate}
                    solicitorForm={solicitorForm}
                    handleChange={handleChange}
                    handleRegister={handleRegister}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* 確認ポップアップの追加 */}
      <AgencyRegistrationPop
        showConfirmationPopup={showConfirmationPopup}
        formData={activeTab === 'solicitors' ? solicitorForm : formData}
        closeConfirmationPopup={closeConfirmationPopup}
        confirmRegistration={confirmRegistration}
        activeTab={activeTab}
        documentTypes={documentTypes}
      />
    </>
  );
};

export default AgencyRegistration;
